import { JSX, useRef } from 'react'

import { SlippageInput } from '~/components/SlippageInput'
import { SelectInput } from '~/components/component-library'
import { PaymentSourcePlaceholder } from '~/components/forms/transfer/fields/PaymentSource/PaymentSourcePlaceholder'
import { PaymentSourceSelect } from '~/components/forms/transfer/fields/PaymentSource/PaymentSourceSelect'
import { PaymentSourceTooltip } from '~/components/forms/transfer/fields/PaymentSource/PaymentSourceTooltip'
import { SlippageTooltip } from '~/components/forms/transfer/fields/SlippageTooltip'
import { SupportedChainId } from '~/constants/chains'
import { IAddress } from '~/types'
import { bpsToPercentage, getDefaultSlippageBps } from '~/utils/swap'

type PaymentSourceFieldProps = {
  // Whether the input has everything it needs to function (a from account, a
  // priced + valid destination token, an amount, and a compatible recipient).
  // Computed by the caller since the recipient shape differs between the
  // single- and multi-send flows. When false, a disabled placeholder renders so
  // the field is always present and the form doesn't jump.
  ready: boolean
  sender: IAddress | undefined
  destinationToken:
    | { token?: IAddress; chainId?: SupportedChainId; amount?: string }
    | undefined
  destinationTokenSymbol?: string
  selectedToken:
    { address: IAddress; chainId: SupportedChainId; amount: string } | undefined
  selectedTokenSymbol?: string
  onSelect: (
    token: { address: IAddress; chainId: SupportedChainId } | undefined,
  ) => void
  // Whether or not this is a private transfer
  isPrivate?: boolean
  quoteError: string | undefined
  disabledReason: string
  // Multisend offers same-chain sources only; single-send allows bridging.
  preventBridge?: boolean
  // Offramp send — keeps the direct-pay row's "amount available" shown (it's the
  // settlement-token balance you can offramp, not a redundant direct-send line).
  isOfframp?: boolean
}

/**
 * The always-visible "Payment source" field shared by the single- and
 * multi-send flows: the populated SelectInput + a swap-only SlippageInput when
 * ready, or a disabled placeholder until the form is filled in.
 */
export const PaymentSourceField = ({
  ready,
  sender,
  destinationToken,
  destinationTokenSymbol,
  selectedToken,
  selectedTokenSymbol,
  onSelect,
  isPrivate,
  quoteError,
  disabledReason,
  preventBridge,
  isOfframp,
}: PaymentSourceFieldProps): JSX.Element => {
  // Held here, not in usePaymentSourceOptions, so it survives PaymentSourceSelect
  // unmounting when the field shows its placeholder (amount briefly empty while
  // editing) — otherwise an auto-selected source never reverts to a direct send.
  const wasAutoSelectedRef = useRef(false)

  // The truthy sender/token/chainId checks are redundant with `ready` (the
  // caller's predicate already validates them) but narrow the types for the
  // PaymentSourceSelect props below.
  if (
    !ready ||
    !sender ||
    !destinationToken?.token ||
    !destinationToken?.chainId
  ) {
    return (
      <SelectInput<string>
        label="Payment source"
        placeholder={<PaymentSourcePlaceholder />}
        tooltip={<PaymentSourceTooltip />}
        disabledReason={disabledReason}
        disabled
        options={[]}
        selectedOption={undefined}
        selectValue={() => undefined}
      />
    )
  }

  return (
    <>
      <PaymentSourceSelect
        sender={sender}
        destinationToken={{
          token: destinationToken.token,
          chainId: destinationToken.chainId,
          amount: destinationToken.amount ?? '0',
        }}
        selectedToken={selectedToken}
        isPrivate={isPrivate}
        onSelect={onSelect}
        preventBridge={preventBridge}
        isOfframp={isOfframp}
        error={quoteError}
        wasAutoSelectedRef={wasAutoSelectedRef}
      />
      {/* Slippage only matters when the source token differs from what the
          recipient gets (i.e. a swap is happening). A direct pay leaves
          `selectedToken` unset, so this stays hidden. */}
      {selectedToken ? (
        <SlippageInput
          inputName="swapData.slippage"
          defaultValue={bpsToPercentage(
            getDefaultSlippageBps(selectedTokenSymbol, destinationTokenSymbol),
          ).toString()}
          tooltip={<SlippageTooltip />}
        />
      ) : null}
    </>
  )
}
